import React, { useMemo, useState } from "react";
import {
  DataGrid,
  GridSortDirection,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { Box, IconButton, styled, Tooltip, Typography } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import { Organization } from "../../../../Types";
import {
  getFormattedMasjidStatus,
  getStatus,
} from "../../PageComponents/MasjidTable/Helper";
import StatusHandlerModal from "../../Shared/StatusHandlerModal/StatusHandlerModal";
import { EntityTableAvatar } from "../../Shared/EntityTableAvatar/EntityTableAvatar";
import { redirectToVanillaPortal } from "../../../helpers/HelperFunction";

const StyledGridOverlay = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  "& .no-rows-primary": {
    fill: "#3D4751",
    ...theme.applyStyles("light", {
      fill: "#AEB8C2",
    }),
  },
  "& .no-rows-secondary": {
    fill: "#1D2126",
    ...theme.applyStyles("light", {
      fill: "#E8EAED",
    }),
  },
}));

function CustomNoRowsOverlay() {
  return (
    <StyledGridOverlay>
      <svg
        width="120"
        height="100"
        viewBox="0 0 184 152"
        aria-hidden
        focusable="false"
      >
        <g fill="none" fillRule="evenodd">
          <g transform="translate(24 31.67)">
            <ellipse
              className="no-rows-primary"
              cx="67.797"
              cy="106.89"
              rx="67.797"
              ry="12.668"
            />
            <path
              className="no-rows-primary"
              d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
            />
            <path
              className="no-rows-secondary"
              d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
            />
            <path
              className="no-rows-primary"
              d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM61.355 48.088h9.168a2 2 0 0 1 2 2v9.168a2 2 0 0 1-2 2h-9.168a2 2 0 0 1-2-2v-9.168a2 2 0 0 1 2-2z"
            />
          </g>
        </g>
      </svg>
      <Box sx={{ mt: 1, color: "text.secondary" }}>
        <Typography variant="body2">No organizations found</Typography>
      </Box>
    </StyledGridOverlay>
  );
}

type OrganizationsToolbarProps = {
  onOpenAdvancedFilter?: () => void;
  hasActiveFilter?: boolean;
};

const OrganizationsToolbar: React.FC<OrganizationsToolbarProps> = ({
  onOpenAdvancedFilter,
  hasActiveFilter,
}) => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarDensitySelector />
    <GridToolbarExport />
    <Box sx={{ flexGrow: 1 }} />
    <GridToolbarQuickFilter debounceMs={500} />
    {onOpenAdvancedFilter && (
      <Tooltip title="Advanced Filter">
        <IconButton
          onClick={onOpenAdvancedFilter}
          color={hasActiveFilter ? "primary" : "default"}
          data-testid="FilterListIcon"
          size="small"
          sx={{ ml: 1 }}
          aria-label="Advanced Filter"
        >
          <FilterListIcon />
          <Typography
            variant="body2"
            sx={{
              ml: 0.5,
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            Advanced Filter
          </Typography>
        </IconButton>
      </Tooltip>
    )}
  </GridToolbarContainer>
);

interface OrganizationsTableProps {
  loading: boolean;
  filteredData: Organization[] | [];
  handleActionClick: (action: any, details: Organization) => void;
  page?: number;
  pageSize?: number;
  rowCount?: number;
  paginationMode?: "client" | "server";
  onPageChange?: (newPage: number) => void;
  onPageSizeChange?: (newPageSize: number) => void;
  sortModel?: { field: string; sort: GridSortDirection }[];
  onSortModelChange?: (newModel: any) => void;
  onFilterModelChange?: (model: any) => void;
  searchText?: string;
  onOpenAdvancedFilter?: () => void;
  hasActiveFilter?: boolean;
}

const OrganizationsTable = ({
  loading,
  filteredData,
  handleActionClick,
  page = 0,
  pageSize = 20,
  rowCount = 0,
  paginationMode = "server",
  onPageChange,
  onPageSizeChange,
  sortModel = [],
  onSortModelChange,
  onFilterModelChange,
  searchText = "",
  onOpenAdvancedFilter,
  hasActiveFilter = false,
}: OrganizationsTableProps) => {
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleRowAction = (organization: Organization) => {
    setSelectedOrganization(organization);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedOrganization(null);
  };

  const handleStatusUpdate = () => {
    // Trigger refetch of data
    if (handleActionClick) {
      handleActionClick("status_updated", selectedOrganization!);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        renderCell: (params: any) => {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
                width: "100%",
              }}
            >
              <EntityTableAvatar
                src={params.row.profilePhoto}
                name={params.row.name}
              />
              <Typography
                onClick={() => {
                  redirectToVanillaPortal(params.id as string, "ORGANIZATION");
                }}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "pointer",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {params.row.name}
              </Typography>
            </Box>
          );
        },
      },
      { field: "organizationType", headerName: "Type", flex: 1 },
      { field: "contact", headerName: "Contact", flex: 1 },
      { field: "address", headerName: "Address", flex: 1 },
      {
        field: "isVerified",
        headerName: "Verification Status",
        flex: 1,
        valueGetter: (params: any) => {
          const value = getStatus(params.row.isFreezed, params.row.isVerified);
          return getFormattedMasjidStatus(value);
        },
        renderCell: (params: any) => {
          let color;
          const value = getStatus(params.row.isFreezed, params.row.isVerified);

          if (value === "approved" || value === null) {
            color = "#1B8368";
          } else if (value === "pending") {
            color = "#EDA920";
          } else {
            color = "#FF4949";
          }

          return (
            <div
              onClick={() => {
                handleRowAction(params.row);
              }}
            >
              <Typography
                style={{
                  color,
                  cursor: "pointer",
                  textTransform: "capitalize",
                }}
                variant="body2"
              >
                {getFormattedMasjidStatus(value)}
              </Typography>
            </div>
          );
        },
      },
      {
        field: "subscribers",
        headerName: "Subscribers",
        flex: 1,
        valueGetter: (params: any) => {
          return params.row.subscribers || 0;
        },
      },
      {
        field: "followers",
        headerName: "Followers",
        flex: 1,
        valueGetter: (params: any) => {
          return params.row.followers || 0;
        },
      },
      {
        field: "createdAt",
        headerName: "Created At",
        flex: 1,
        valueGetter: (params: any) => {
          const value = params.row.createdAt;
          return value
            ? moment.utc(value).local().format("DD-MMM-YYYY HH:mm A")
            : "-";
        },
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        flex: 1,
        valueGetter: (params: any) => {
          const value = params.row.updatedAt;
          return value
            ? moment.utc(value).local().format("DD-MMM-YYYY HH:mm A")
            : "-";
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0,
        sortable: false,
        renderCell: (params: any) => {
          return (
            <IconButton onClick={() => handleRowAction(params.row)}>
              <EditIcon />
            </IconButton>
          );
        },
      },
    ],
    [handleActionClick]
  );

  return (
    <>
      <div style={{ height: loading ? "650px" : "1200px", width: "100%" }}>
        <DataGrid
          style={{ backgroundColor: "white" }}
          loading={loading}
          rows={filteredData}
          columns={columns}
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          pagination
          paginationMode={paginationMode}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          filterMode="server"
          onFilterModelChange={onFilterModelChange}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          components={{
            Toolbar: OrganizationsToolbar,
            NoRowsOverlay: CustomNoRowsOverlay,
          }}
          componentsProps={{
            toolbar: {
              onOpenAdvancedFilter,
              hasActiveFilter,
            },
          }}
          disableSelectionOnClick
          getRowId={(row) => row._id}
          getRowClassName={(params) => {
            return params.row?.isFreezed ? "frozen-organization-row" : "";
          }}
          rowsPerPageOptions={[5, 10, 20]}
        />
      </div>

      <StatusHandlerModal
        open={modalOpen}
        onClose={handleModalClose}
        entity={selectedOrganization}
        entityType="ORGANIZATION"
        onStatusUpdate={handleStatusUpdate}
      />
    </>
  );
};

export default OrganizationsTable;
